import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, FlaskConical, Keyboard, Plus, Ruler, Trash2 } from 'lucide-react';
import { LOINC_CODE_PATTERN } from '@his/shared';
import { post } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { cn, humanize } from '@/lib/utils';
import { formatMoneyInput, minorFromInput } from '@/components/MoneyInput';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { RecordState } from '@/components/ui/record-state';
import { Alert, Button, Checkbox, Field, Input, Select } from '@/components/ui/primitives';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { useToast } from '@/components/ui/toast';
import { safeReturnTo } from '../opd/BookAppointmentPage';
import { getPages } from './pagedList';
import type { LabTestRow } from './LabCataloguePage';

const GROUPS = ['hematology', 'biochemistry', 'microbiology', 'serology', 'histopathology', 'molecular', 'other'] as const;
const RESULT_TYPES = ['numeric', 'text', 'select', 'culture'] as const;
const DAYS_PER_YEAR = 365;
const MAX_AGE_DAYS = 73_000;

const toYears = (days: number) => Math.round((days / DAYS_PER_YEAR) * 10) / 10;
const toDays = (years: number) => Math.round(years * DAYS_PER_YEAR);
const numberOrNull = (raw: string): number | null => (raw.trim() === '' ? null : Number(raw));

type RangeDraft = { id: string; sex: 'any' | 'male' | 'female'; minYears: string; maxYears: string; low: string; high: string; text: string };

let rangeSequence = 0;
const nextRangeId = () => `range-${rangeSequence++}`;

function initialForm(test: LabTestRow | null) {
  return {
    code: test?.code ?? '',
    name: test?.name ?? '',
    departmentGroup: test?.departmentGroup ?? 'biochemistry',
    specimenType: test?.specimenType ?? 'Serum',
    containerType: test?.containerType ?? '',
    tatMinutes: String(test?.tatMinutes ?? 240),
    unit: test?.unit ?? '',
    resultType: test?.resultType ?? 'numeric',
    selectOptions: (test?.selectOptions ?? []).join(', '),
    criticalLow: test?.criticalLow === null || test?.criticalLow === undefined ? '' : String(test.criticalLow),
    criticalHigh: test?.criticalHigh === null || test?.criticalHigh === undefined ? '' : String(test.criticalHigh),
    reportableLow: test?.reportableLow === null || test?.reportableLow === undefined ? '' : String(test.reportableLow),
    reportableHigh: test?.reportableHigh === null || test?.reportableHigh === undefined ? '' : String(test.reportableHigh),
    method: test?.method ?? '',
    price: formatMoneyInput(test?.basePriceMinor ?? 0),
    isOutsourced: test?.isOutsourced ?? false,
    decimals: test?.decimals === null || test?.decimals === undefined ? '' : String(test.decimals),
    quickValues: (test?.quickValues ?? []).join(', '),
    defaultValue: test?.defaultValue ?? '',
    loincCode: test?.loincCode ?? '',
    sortOrder: String(test?.sortOrder ?? 0),
    isActive: test?.isActive ?? true,
  };
}

function initialRanges(test: LabTestRow | null): RangeDraft[] {
  return (test?.referenceRanges ?? []).map((r) => ({
    id: nextRangeId(),
    sex: r.sex,
    minYears: String(toYears(r.minAgeDays ?? 0)),
    maxYears: (r.maxAgeDays ?? MAX_AGE_DAYS) >= MAX_AGE_DAYS ? '' : String(toYears(r.maxAgeDays)),
    low: r.low === null || r.low === undefined ? '' : String(r.low),
    high: r.high === null || r.high === undefined ? '' : String(r.high),
    text: r.text ?? '',
  }));
}

export function LabTestPage() {
  const { testId } = useParams<{ testId: string }>();
  const [params] = useSearchParams();
  const returnTo = safeReturnTo(params.get('returnTo'), '/lab/catalogue?tab=tests');

  const tests = useQuery({
    queryKey: ['lab-catalogue-tests'],
    queryFn: () => getPages<LabTestRow>('/lab/tests', { includeInactive: true, limit: 500 }, 4),
    enabled: Boolean(testId),
  });

  const test = testId ? (tests.data?.find((row) => row.id === testId) ?? null) : null;

  if (testId && !test) {
    return (
      <FormScreen title={tr('lab', 'test_catalogue')} icon={FlaskConical} backTo={returnTo}>
        <RecordState error={tests.error} missing={!tests.isLoading} label={tr('lab', 'loading')} />
      </FormScreen>
    );
  }

  return <TestForm key={test?.id ?? 'new'} test={test} returnTo={returnTo} />;
}

function TestForm({ test, returnTo }: { test: LabTestRow | null; returnTo: string }) {
  const { hasModule } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [initial] = useState(() => ({ form: initialForm(test), ranges: initialRanges(test), abnormal: test?.abnormalOptions ?? [] }));
  const [form, setForm] = useState(initial.form);
  const [ranges, setRanges] = useState<RangeDraft[]>(initial.ranges);
  const [abnormal, setAbnormal] = useState<string[]>(initial.abnormal);
  const [error, setError] = useState<string | null>(null);

  const numeric = form.resultType === 'numeric';
  const choices = form.selectOptions.split(',').map((o) => o.trim()).filter(Boolean);
  const reportableLow = numberOrNull(form.reportableLow);
  const reportableHigh = numberOrNull(form.reportableHigh);
  const reportableBackwards = numeric && reportableLow !== null && reportableHigh !== null && reportableLow >= reportableHigh;
  const loincBad = form.loincCode.trim() !== '' && !LOINC_CODE_PATTERN.test(form.loincCode.trim());

  const save = useMutation({
    mutationFn: () =>
      post('/lab/tests', {
        ...(test ? { id: test.id } : {}),
        code: form.code.trim().toUpperCase(),
        name: form.name.trim(),
        departmentGroup: form.departmentGroup,
        specimenType: form.specimenType.trim(),
        containerType: form.containerType.trim() || undefined,
        tatMinutes: Number(form.tatMinutes),
        unit: form.unit.trim() || undefined,
        resultType: form.resultType,
        selectOptions: form.resultType === 'select' ? choices : [],
        abnormalOptions: form.resultType === 'select' ? abnormal.filter((option) => choices.includes(option)) : [],
        referenceRanges: ranges.map((r) => ({
          sex: r.sex,
          minAgeDays: toDays(Number(r.minYears || 0)),
          maxAgeDays: r.maxYears.trim() === '' ? MAX_AGE_DAYS : toDays(Number(r.maxYears)),
          low: numeric ? numberOrNull(r.low) : null,
          high: numeric ? numberOrNull(r.high) : null,
          text: r.text.trim() || undefined,
        })),
        criticalLow: numeric ? numberOrNull(form.criticalLow) : null,
        criticalHigh: numeric ? numberOrNull(form.criticalHigh) : null,
        reportableLow: numeric ? reportableLow : null,
        reportableHigh: numeric ? reportableHigh : null,
        method: form.method.trim() || undefined,
        basePriceMinor: minorFromInput(form.price),
        isOutsourced: form.isOutsourced,
        decimals: numeric && form.decimals.trim() !== '' ? Number(form.decimals) : null,
        quickValues: form.quickValues.split(',').map((v) => v.trim()).filter(Boolean),
        defaultValue: form.defaultValue.trim() || null,
        loincCode: form.loincCode.trim() || null,
        sortOrder: Number(form.sortOrder) || 0,
        isActive: form.isActive,
      }),
    onSuccess: () => {
      toast.success(test ? tr('lab', 'test_updated') : tr('lab', 'test_added'));
      void queryClient.invalidateQueries({ queryKey: ['lab-catalogue-tests'] });
      void queryClient.invalidateQueries({ queryKey: ['lab-tests-search'] });
      navigate(returnTo, { replace: true });
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const set = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) => setForm((f) => ({ ...f, [key]: value }));
  const setRange = (index: number, patch: Partial<RangeDraft>) =>
    setRanges((current) => current.map((r, i) => (i === index ? { ...r, ...patch } : r)));

  const ready =
    form.code.trim().length >= 2 && form.name.trim().length >= 2 && form.specimenType.trim().length >= 2 && !reportableBackwards && !loincBad;
  const dirty =
    !save.isSuccess &&
    (JSON.stringify(form) !== JSON.stringify(initial.form) ||
      JSON.stringify(ranges) !== JSON.stringify(initial.ranges) ||
      JSON.stringify(abnormal) !== JSON.stringify(initial.abnormal));

  return (
    <FormScreen
      title={test ? tr('lab', 'edit_x', { name: test.name }) : tr('lab', 'add_a_test')}
      description={tr('lab', 'how_the_result_is_entered_what_is_normal_for')}
      eyebrow={tr('lab', 'test_catalogue')}
      icon={FlaskConical}
      backTo={returnTo}
      dirty={dirty}
      wide
      onSubmit={(event) => {
        event.preventDefault();
        if (!ready) return;
        setError(null);
        save.mutate();
      }}
    >
      <FormSection title={tr('lab', 'test')} icon={FlaskConical} columns={3}>
        <Field label={tr('lab', 'code')} required hint={tr('lab', 'analysers_send_this_code')}>
          <Input value={form.code} onChange={(e) => set('code', e.target.value.toUpperCase())} placeholder={tr('lab', 'hb')} className="font-mono" />
        </Field>
        <Field label={tr('lab', 'name')} required className="sm:col-span-2">
          <Input value={form.name} onChange={(e) => set('name', e.target.value)} placeholder={tr('lab', 'haemoglobin')} />
        </Field>
        <Field label={tr('lab', 'department')}>
          <Select value={form.departmentGroup} onChange={(e) => set('departmentGroup', e.target.value)}>
            {GROUPS.map((g) => (
              <option key={g} value={g}>
                {humanize(g)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={tr('lab', 'specimen')} required>
          <Input value={form.specimenType} onChange={(e) => set('specimenType', e.target.value)} placeholder={tr('lab', 'whole_blood_edta')} />
        </Field>
        <Field label={tr('lab', 'container')}>
          <Input value={form.containerType} onChange={(e) => set('containerType', e.target.value)} placeholder={tr('lab', 'lavender_top')} />
        </Field>
        <Field label={tr('lab', 'method')}>
          <Input value={form.method} onChange={(e) => set('method', e.target.value)} placeholder={tr('lab', 'cyanmethaemoglobin')} />
        </Field>
        <Field label={tr('lab', 'turnaround_minutes')}>
          <Input type="number" min={5} value={form.tatMinutes} onChange={(e) => set('tatMinutes', e.target.value)} />
        </Field>
        <Field label={tr('lab', 'price')}>
          <Input type="text" dir="ltr" inputMode="decimal" value={form.price} onChange={(e) => set('price', e.target.value)} />
        </Field>
        <Field label={tr('lab', 'sort_order')} hint={tr('lab', 'lower_comes_first_on_sheets_and_reports')}>
          <Input type="number" min={0} value={form.sortOrder} onChange={(e) => set('sortOrder', e.target.value)} />
        </Field>
        {hasModule('insurance') && (
          <Field
            label={tr('lab', 'loinc_code')}
            hint={hasModule('nphies') ? tr('lab', 'sent_with_each_released_result_on_an_nphies') : tr('lab', 'the_international_code_insurers_use_for_this')}
            error={loincBad ? 'Enter a LOINC code such as 2345-7' : undefined}
          >
            <Input value={form.loincCode} onChange={(e) => set('loincCode', e.target.value)} placeholder="718-7" className="font-mono" />
          </Field>
        )}
        <div className="flex flex-col justify-end gap-2 text-sm">
          <Checkbox checked={form.isOutsourced} onCheckedChange={(checked) => set('isOutsourced', checked)} label={tr('lab', 'sent_out_to_a_reference_lab')} />
          <Checkbox checked={form.isActive} onCheckedChange={(checked) => set('isActive', checked)} label={tr('lab', 'active_orderable')} />
        </div>
      </FormSection>

      <FormSection title={tr('lab', 'result')} icon={Keyboard} columns={3}>
        <Field label={tr('lab', 'result_type')}>
          <ChoiceButtons<string>
            layout="chips"
            value={form.resultType}
            onChange={(value) => set('resultType', value)}
            options={RESULT_TYPES.map((t) => ({ value: t, label: humanize(t) }))}
          />
        </Field>
        <Field label={tr('lab', 'unit')}>
          <Input value={form.unit} onChange={(e) => set('unit', e.target.value)} placeholder={tr('lab', 'g_dl')} disabled={!numeric} />
        </Field>
        <Field label={tr('lab', 'decimals')} hint={tr('lab', 'digits_after_the_point_on_the_report')}>
          <Input type="number" min={0} max={4} value={form.decimals} onChange={(e) => set('decimals', e.target.value)} disabled={!numeric} />
        </Field>
        {form.resultType === 'select' && (
          <Field label={tr('lab', 'choices')} hint={tr('lab', 'comma_separated')} className="sm:col-span-2 lg:col-span-3">
            <Input value={form.selectOptions} onChange={(e) => set('selectOptions', e.target.value)} placeholder={tr('lab', 'negative_positive_reactive')} />
          </Field>
        )}
        {form.resultType === 'select' && (
          <Field label={tr('lab', 'abnormal_options')} hint={tr('lab', 'choose_the_choices_above_that_make_a_result')} className="sm:col-span-2 lg:col-span-3">
            <div className="flex flex-wrap gap-2">
              {choices.length === 0 && <p className="text-sm text-muted-foreground">{tr('lab', 'type_the_choices_above_first')}</p>}
              {choices.map((choice) => {
                const marked = abnormal.includes(choice);
                return (
                  <button
                    key={choice}
                    type="button"
                    aria-pressed={marked}
                    onClick={() => setAbnormal((current) => (marked ? current.filter((o) => o !== choice) : [...current, choice]))}
                    className={cn(
                      'rounded-full border px-3 py-1 text-xs font-medium transition-colors focus-ring',
                      marked ? 'border-destructive/40 bg-destructive/10 text-destructive' : 'border-input bg-background text-foreground hover:bg-accent',
                    )}
                  >
                    {choice}
                  </button>
                );
              })}
            </div>
          </Field>
        )}
        <Field label={tr('lab', 'quick_values')} hint={tr('lab', 'comma_separated_one_click_enters_the_value_on')} className="sm:col-span-2">
          <Input value={form.quickValues} onChange={(e) => set('quickValues', e.target.value)} placeholder={tr('lab', 'negative_positive_nil_trace')} />
        </Field>
        <Field label={tr('lab', 'default_value')} hint={tr('lab', 'pre_filled_on_the_sheet_until_someone_types')}>
          <Input value={form.defaultValue} onChange={(e) => set('defaultValue', e.target.value)} placeholder={tr('lab', 'negative')} />
        </Field>
      </FormSection>

      <FormSection
        title={tr('lab', 'reference_ranges')}
        description={numeric ? tr('lab', 'by_sex_and_age_the_first_matching_band_prints') : tr('lab', 'the_expected_result_e_g_negative_printed_on_the')}
        icon={Ruler}
        columns={1}
      >
        {ranges.length === 0 && <p className="text-sm text-muted-foreground">{tr('lab', 'no_reference_range_results_are_never_flagged')}</p>}
        {ranges.map((range, index) => (
          <div key={range.id} className="grid items-end gap-2 sm:grid-cols-12">
            <div className="sm:col-span-3">
              <ChoiceButtons<RangeDraft['sex']>
                label={tr('opd', 'sex')}
                value={range.sex}
                onChange={(value) => setRange(index, { sex: value })}
                size="sm"
                options={[
                  { value: 'any', label: tr('lab', 'any_sex') },
                  { value: 'male', label: tr('lab', 'male') },
                  { value: 'female', label: tr('lab', 'female') },
                ]}
              />
            </div>
            <div className="sm:col-span-2">
              <Input type="number" min={0} step="0.1" placeholder={tr('lab', 'from_years')} value={range.minYears} onChange={(e) => setRange(index, { minYears: e.target.value })} />
            </div>
            <div className="sm:col-span-2">
              <Input type="number" min={0} step="0.1" placeholder={tr('lab', 'to_years')} value={range.maxYears} onChange={(e) => setRange(index, { maxYears: e.target.value })} />
            </div>
            {numeric ? (
              <>
                <div className="sm:col-span-2">
                  <Input type="number" step="any" placeholder={tr('lab', 'low')} value={range.low} onChange={(e) => setRange(index, { low: e.target.value })} />
                </div>
                <div className="sm:col-span-2">
                  <Input type="number" step="any" placeholder={tr('lab', 'high')} value={range.high} onChange={(e) => setRange(index, { high: e.target.value })} />
                </div>
              </>
            ) : (
              <div className="sm:col-span-4">
                <Input placeholder={tr('lab', 'expected_e_g_negative')} value={range.text} onChange={(e) => setRange(index, { text: e.target.value })} />
              </div>
            )}
            <div className="sm:col-span-1">
              <Button type="button" variant="ghost" size="icon" aria-label={tr('lab', 'remove_band')} onClick={() => setRanges((r) => r.filter((_, i) => i !== index))}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ))}
        <div>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => setRanges((r) => [...r, { id: nextRangeId(), sex: 'any', minYears: '0', maxYears: '', low: '', high: '', text: '' }])}
          >
            <Plus className="me-1 h-3.5 w-3.5" /> {tr('lab', 'add_band')}
          </Button>
        </div>
      </FormSection>

      {numeric && (
        <FormSection title={tr('lab', 'critical_low')} icon={AlertTriangle}>
          <Field label={tr('lab', 'critical_low')} hint={tr('lab', 'at_or_below_the_ordering_doctor_is_paged')}>
            <Input type="number" step="any" value={form.criticalLow} onChange={(e) => set('criticalLow', e.target.value)} />
          </Field>
          <Field label={tr('lab', 'critical_high')} hint={tr('lab', 'at_or_above')}>
            <Input type="number" step="any" value={form.criticalHigh} onChange={(e) => set('criticalHigh', e.target.value)} />
          </Field>
          <Field label={tr('lab', 'reportable_low')} hint={tr('lab', 'optional_a_result_below_this_is_refused_at')}>
            <Input type="number" step="any" value={form.reportableLow} onChange={(e) => set('reportableLow', e.target.value)} />
          </Field>
          <Field
            label={tr('lab', 'reportable_high')}
            hint={tr('lab', 'optional_a_result_above_this_is_refused_at')}
            error={reportableBackwards ? 'Must be above the reportable low' : undefined}
          >
            <Input type="number" step="any" value={form.reportableHigh} onChange={(e) => set('reportableHigh', e.target.value)} />
          </Field>
        </FormSection>
      )}

      {error && <Alert variant="destructive">{error}</Alert>}

      <FormActions submitLabel={test ? tr('lab', 'save_changes') : tr('lab', 'add_test')} loading={save.isPending} incomplete={!ready} cancelTo={returnTo} />
    </FormScreen>
  );
}
